import { formatarMoedaPlano } from './gerar-mensagem-proposta.js';

export interface LinhaComComplementos {
  tipo: string;
  plano?: { nome: string };
  planoId?: string | null;
  categoriaCustom?: string;
  quantidade?: number;
  linhaVinculada?: string;
  numero?: string;
  adicionais?: Record<string, number>;
  aparelho?: { nome: string; preco: number; parcela?: number; forma: string };
}

export const NOMES_ADICIONAIS_MOVEL: Record<string, string> = {
  'passaport-america': 'Passaport América',
  'passaport-europa': 'Passaport Europa',
  'passaport-mundo': 'Passaport Mundo',
};

export function listarServicosDigitais(linhas: LinhaComComplementos[]): string[] {
  const porOpcao = new Map<string, { nome: string; licencas: number; linhas: string[] }>();

  for (const l of linhas) {
    if (l.tipo !== 'SERVICO' || !l.plano) continue;
    const chave = `${l.categoriaCustom ?? ''}::${l.planoId ?? l.plano.nome}`;
    const atual = porOpcao.get(chave) ?? { nome: l.plano.nome, licencas: 0, linhas: [] };
    atual.licencas += Math.max(1, Math.floor(Number(l.quantidade) || 1));
    const vinculo = String(l.linhaVinculada ?? '').replace(/\D/g, '');
    if (vinculo.length >= 10 && !atual.linhas.includes(vinculo)) atual.linhas.push(vinculo);
    porOpcao.set(chave, atual);
  }

  return Array.from(porOpcao.values()).map(({ nome, licencas, linhas: vinculadas }) => {
    const qtd = licencas > 1 ? `${licencas}× ` : '';
    const onde = vinculadas.length === 1
      ? ` (linha ${formatarTelefoneExibicao(vinculadas[0])})`
      : vinculadas.length > 1
        ? ` (${vinculadas.length} linhas)`
        : '';
    return `• ${qtd}${nome}${onde}`;
  });
}

/** (51) 99999-9999 — só para exibição na mensagem */
export function formatarTelefoneExibicao(digitos: string): string {
  const d = digitos.replace(/\D/g, '').replace(/^55/, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return digitos;
}

/**
 * Aparelhos da composicao, para as mensagens que vao AO CLIENTE.
 *
 * (Douglas 21/08/2026) O aparelho entrava no pedido mas nao aparecia em lugar
 * nenhum do que o cliente le: "nem na proposta, nem no fechamento, nao vai pro
 * cliente". Vendia-se um iPhone que a mensagem nao mencionava.
 *
 * O valor NAO entra no total mensal do plano — aparelho e parcelado na fatura e
 * e' separado do faturamento por regra (ver flagsFaturamento). Por isso a forma
 * de pagamento vai escrita: o cliente precisa saber o que vai somar na conta.
 */
export function listarAparelhos(linhas: LinhaComComplementos[]): Array<{ texto: string; nome: string; forma: string; linha: string }> {
  const saida: Array<{ texto: string; nome: string; forma: string; linha: string }> = [];
  for (const l of linhas) {
    const ap = l.aparelho;
    if (!ap) continue;
    const parcela = ap.parcela ?? (ap.forma === 'avista' ? ap.preco : ap.preco / (ap.forma === '24x' ? 24 : 12));
    const forma = ap.forma === 'avista'
      ? `${formatarMoedaPlano(ap.preco)} a vista`
      : `${ap.forma} de ${formatarMoedaPlano(parcela)} na fatura`;
    const tel = String(l.numero ?? '').replace(/\D/g, '');
    const onde = tel.length >= 10 ? ` (linha ${formatarTelefoneExibicao(tel)})` : '';
    saida.push({ texto: `${ap.nome} — ${forma}${onde}`, nome: ap.nome, forma, linha: onde });
  }
  return saida;
}

export function montarComplementosProposta(
  linhas: LinhaComComplementos[],
  nomeDoAdicional: (id: string) => string | undefined = id => NOMES_ADICIONAIS_MOVEL[id],
) {
  const tv = linhas.find(l => l.tipo === 'TV');
  const passaports = new Set<string>();
  for (const l of linhas.filter(l => ['TT', 'TT_PRE', 'PORTABILIDADE', 'RENOVACAO', 'LINHA_NOVA'].includes(l.tipo))) {
    for (const [id, quantidade] of Object.entries(l.adicionais ?? {})) {
      const nome = nomeDoAdicional(id);
      if (nome && quantidade > 0) passaports.add(nome);
    }
  }
  return {
    servicosDigitais: listarServicosDigitais(linhas),
    aparelhos: listarAparelhos(linhas).map(a => a.texto),
    passaports: [...passaports],
    pontosTV: Object.values(tv?.adicionais ?? {}).reduce((total, n) => total + n, 0),
  };
}
